import { animated } from '@react-spring/web';
import { useNavigate, useParams } from 'react-router-dom';
import { useCallback, useEffect, useState, type JSX } from 'react';
import backButtonDark from '@/assets/svg/static/back-button-dark.svg';
import logoFull from '@/assets/svg/static/logo-full.svg';
import {
  DoubleColumnProductList,
  FilterBar,
  SimilarProductImageCarousel,
  type FilterTags,
  type PriceFilterTags,
} from '@/components/features';
import { BottomSheet } from '@/components/layout';
import { PRICE_RANGES } from '@/constants/priceFilterRanges';
import { fetchCurationById } from '@/services/slaydAPI';
import useLayoutStore from '@/stores/useLayoutStore';
import type { CurationResponse } from '@/types/Curation.types';
import type { Product } from '@/types/Product.types';
import { getMinMaxPriceRange } from '@/utils/formatting';
import type { CurationImages, CurationProducts } from './CurationPage.types';
import styles from './CurationsPage.module.css';

const CurationsPage = (): JSX.Element => {
  const [imageUrls, setImageUrls] = useState<CurationImages[]>([]);
  const [frameInFocus, setFrameInFocus] = useState<number>(0);
  const navigate = useNavigate();
  const [products, setProducts] = useState<CurationProducts[]>([]);
  const [productsToDiplay, setProductsToDisplay] = useState<Product[]>([]);
  const [priceFiltersToDisplay, setPriceFiltersToDisplay] =
    useState<PriceFilterTags[]>(PRICE_RANGES);
  const [filtersToDisplay, setFiltersToDisplay] = useState<FilterTags[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const setNavbarVisibility = useLayoutStore(
    (state) => state.setNavbarVisibility
  );

  const { id } = useParams();

  // Manage navbar visibility
  useEffect(() => {
    setNavbarVisibility(false);

    // return a cleanup function to reset the navbar's visibility when the user navigates away from this page.
    return () => {
      setNavbarVisibility(true);
    };
  }, [setNavbarVisibility]);

  useEffect(() => {
    const fetchCuration = async (): Promise<void> => {
      if (!id) return;
      try {
        const curationData = await fetchCurationById(id);

        const idNum = Number(id);

        let frames: CurationImages[] = [];
        let productsArray: CurationProducts[] = [];

        // Add main curationImage first
        if (curationData.curationImage) {
          frames = [{ id: idNum, imageUrl: curationData.curationImage }];
        }

        if (
          curationData.relatedCurations &&
          curationData.relatedCurations.length > 0
        ) {
          const relatedImages: CurationImages[] = curationData.relatedCurations
            .map((curation: CurationResponse): CurationImages => {
              return { id: curation.id, imageUrl: curation.curationImage };
            })
            .filter((img) => img.imageUrl !== '');

          frames = [...frames, ...relatedImages];

          productsArray = curationData.relatedCurations.map((curation) => ({
            id: curation.id,
            products: curation.products || [],
            filters: [],
            priceFilters: PRICE_RANGES,
          }));

          // Add a new object with jumbled products
          if (curationData.curationImage) {
            let allProducts = curationData.relatedCurations.flatMap(
              (curation) => curation.products || []
            );

            allProducts = allProducts.sort(() => Math.random() - 0.5);

            productsArray.unshift({
              id: idNum,
              products: allProducts,
              filters: [],
              priceFilters: PRICE_RANGES,
            });
          }
        } else {
          productsArray = [
            {
              id: idNum,
              products: curationData.products,
              filters: [],
              priceFilters: PRICE_RANGES,
            },
          ];
        }

        setImageUrls(frames);
        setProducts(productsArray);
        setProductsToDisplay(productsArray[0].products);
        setFiltersToDisplay(productsArray[0].filters);
        setPriceFiltersToDisplay(productsArray[0].priceFilters);
      } catch (error) {
        console.error('Fetch error:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchCuration();
  }, [id]);

  const handleImageFocusChange = useCallback(
    (index: number) => {
      if (index === frameInFocus || imageUrls.length === 0 || index < 0) return;

      setIsLoading(true);
      const selectedCurationData = products.find(
        (curationProduct) => curationProduct.id === imageUrls[index].id
      );
      if (!selectedCurationData) {
        setIsLoading(false);
        return;
      }
      setProductsToDisplay(selectedCurationData.products);
      setFiltersToDisplay(selectedCurationData.filters);
      setPriceFiltersToDisplay(selectedCurationData.priceFilters);
      setFrameInFocus(index);
      setIsLoading(false);
    },
    [frameInFocus, imageUrls, products]
  );

  const handlePriceFilterChange = useCallback(
    (selectedFilters: PriceFilterTags[]) => {
      const curationData = products;
      const newCurationData = curationData.map((curation) => {
        if (curation.id === imageUrls[frameInFocus].id) {
          curation.priceFilters = selectedFilters;
        }
        return curation;
      });
      setProducts(newCurationData);
      setPriceFiltersToDisplay(selectedFilters);
    },
    [frameInFocus, imageUrls, products]
  );

  const getProductsToDisplay = useCallback(() => {
    const { minPrice, maxPrice } = getMinMaxPriceRange(priceFiltersToDisplay);
    const productsToShow = productsToDiplay.filter((val) => {
      const itemPrice = Number(val.discountPrice || val.price);
      if (itemPrice <= maxPrice && itemPrice >= minPrice) return true;
      return false;
    });
    return productsToShow;
  }, [priceFiltersToDisplay, productsToDiplay]);

  return (
    <div className={styles.container}>
      <animated.div className={styles.imageDisplayContainer}>
        <SimilarProductImageCarousel
          imageUrls={imageUrls.map((curationImages) => curationImages.imageUrl)}
          focusedImage={frameInFocus}
          isGeneratingBoxes={false}
          boxes={[]}
          focusedBox={null}
          onBoxFocus={() => {}}
          onBoxResize={() => {}}
          onBoxResizeStop={() => {}}
          onImageFocusChange={handleImageFocusChange}
        />
      </animated.div>
      <BottomSheet onExpandedChange={setNavbarVisibility}>
        <FilterBar
          filters={filtersToDisplay}
          priceFilter={priceFiltersToDisplay}
          onFilterClick={() => {}}
          onPriceFilterChange={handlePriceFilterChange}
        />
        <DoubleColumnProductList
          products={getProductsToDisplay()}
          className={styles.productListContainer}
          isLoading={isLoading}
          hasMore={false}
          onLoadMore={() => {}}
        />
      </BottomSheet>
      <div className={styles.logoContainer}>
        <img
          src={logoFull}
          alt="Slayd Logo"
          className={styles.logo}
          onClick={() => navigate('/')}
        />
        <button className={styles.backButton} onClick={() => navigate(-1)}>
          <img src={backButtonDark} alt="Back" className={styles.backIcon} />
        </button>
      </div>
    </div>
  );
};

export default CurationsPage;
